import React, { useEffect, useState } from "react";
import { Package, Trash2, Edit3, Loader2, Search, Filter } from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const ITEMS_PER_PAGE = 10;

const ManageStock = () => {
  const [stock, setStock] = useState([]);
  const [filteredStock, setFilteredStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [filterOption, setFilterOption] = useState("latest");
  const [editItem, setEditItem] = useState(null);

  // 🟢 Fetch Stock
  useEffect(() => {
    const fetchStock = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${SERVER_URL}/api/stock`);
        setStock(res.data);
        setFilteredStock(res.data);
      } catch {
        toast.error("❌ Failed to fetch stock data");
      } finally {
        setLoading(false);
      }
    };
    fetchStock();
  }, []);

  // 🔍 Search Filter
  useEffect(() => {
    const filtered = stock.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredStock(filtered);
    setPage(1);
  }, [searchTerm, stock]);

  // ⚙️ Sorting / Filter Options
  // useEffect(() => {
  //   let sorted = [...filteredStock];
  //   if (filterOption === "latest") {
  //     sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  //   } else if (filterOption === "oldest") {
  //     sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  //   } else if (filterOption === "lowStock") {
  //     sorted.sort((a, b) => a.quantity - b.quantity);
  //   } else if (filterOption === "highStock") {
  //     sorted.sort((a, b) => b.quantity - a.quantity);
  //   }
  //   setFilteredStock(sorted);
  // }, [filterOption]);
  // ⚙️ Sorting / Filter Options
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let sorted = [...filteredStock];
    if (filterOption === "latest") {
      sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (filterOption === "oldest") {
      sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else if (filterOption === "lowStock") {
      sorted.sort((a, b) => a.quantity - b.quantity);
    } else if (filterOption === "highStock") {
      sorted.sort((a, b) => b.quantity - a.quantity);
    }
    setFilteredStock(sorted);
  }, [filterOption]);

  // ✅ Edit Stock
  const handleEdit = (item) => setEditItem(item);

  const handleUpdate = async () => {
    if (!editItem.name || !editItem.quantity)
      return toast.error("⚠️ All fields required!");
    try {
      const res = await axios.put(
        `${SERVER_URL}/api/stock/${editItem._id}`,
        editItem
      );
      setStock(stock.map((s) => (s._id === editItem._id ? res.data : s)));
      setEditItem(null);
      toast.success("✏️ Stock updated successfully!");
    } catch {
      toast.error("❌ Error updating stock!");
    }
  };

  // ❌ Delete Stock
  const handleDelete = async (id) => {
    if (window.confirm("Delete this stock item?")) {
      try {
        await axios.delete(`${SERVER_URL}/api/stock/${id}`);
        setStock(stock.filter((s) => s._id !== id));
        toast.success("🗑️ Stock deleted!");
      } catch {
        toast.error("❌ Error deleting stock!");
      }
    }
  };

  // 📑 Pagination
  const totalPages = Math.ceil(filteredStock.length / ITEMS_PER_PAGE);
  const currentPageItems = filteredStock.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  return (
    <div className="min-h-screen to-white">
      {/* <Toaster position="top-right" reverseOrder={false} /> */}

      {/* Header */}
      <div className="flex items-center justify-between mb-10">
        <h1 className="text-3xl font-bold text-green-800 flex items-center gap-2">
          <Package size={30} className="text-green-700" /> Admin Stock
          Management
        </h1>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-8">
        <div className="flex items-center border border-gray-300 rounded-lg px-4 py-2 bg-white max-w-md w-full">
          <Search className="text-green-600 mr-2" size={20} />
          <input
            type="text"
            placeholder="Search product..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full outline-none"
          />
        </div>

        <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2">
          <Filter className="text-green-600" size={18} />
          <select
            value={filterOption}
            onChange={(e) => setFilterOption(e.target.value)}
            className="outline-none bg-transparent text-gray-700"
          >
            <option value="latest">Latest Added</option>
            <option value="oldest">Oldest First</option>
            <option value="highStock">High Stock</option>
            <option value="lowStock">Low Stock</option>
          </select>
        </div>
      </div>

      {/* Stock Table */}
      <div className="bg-white shadow-md rounded-2xl p-6 border border-green-100">
        <h2 className="text-xl font-semibold text-green-700 mb-6">
          Current Stock
        </h2>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 size={28} className="animate-spin text-green-600" />
          </div>
        ) : currentPageItems.length === 0 ? (
          <p className="text-gray-500 text-center py-6">
            No stock records found.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="bg-green-100 text-green-800 text-left">
                    <th className="py-3 px-4">Product Name</th>
                    <th className="py-3 px-4">Quantity</th>
                    <th className="py-3 px-4">BV</th>
                    <th className="py-3 px-4">Added By</th>
                    <th className="py-3 px-4">Date Added</th>
                    <th className="py-3 px-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPageItems.map((item) => (
                    <tr key={item._id} className="border-b hover:bg-green-50">
                      <td className="py-3 px-4">{item.name}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold shadow-sm transition-all duration-300 ${
                            item.quantity <= 10
                              ? "bg-red-100 text-red-700 border border-red-200 animate-pulse"
                              : item.quantity < 20
                              ? "bg-orange-100 text-orange-700 border border-orange-200"
                              : "bg-green-100 text-green-700 border border-green-200"
                          }`}
                        >
                          {item.quantity}
                        </span>
                      </td>

                      <td className="py-3 px-4">{item.bv}</td>
                      <td className="py-3 px-4 text-gray-700">
                        {item.addedBy
                          ? item.addedBy.username || item.addedBy.email
                          : "—"}
                      </td>
                      <td className="py-3 px-4 text-gray-500">
                        {new Date(item.createdAt).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 flex justify-center gap-3">
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-yellow-500 hover:text-yellow-600"
                        >
                          <Edit3 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(item._id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-center mt-6 gap-3">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="px-4 py-2 bg-green-100 hover:bg-green-200 rounded-lg text-green-700 disabled:opacity-40"
              >
                Prev
              </button>
              <span className="px-3 py-2 text-green-800 font-semibold">
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                className="px-4 py-2 bg-green-100 hover:bg-green-200 rounded-lg text-green-700 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editItem && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 shadow-lg w-96">
            <h2 className="text-xl font-semibold mb-4 text-green-700">
              Edit Stock Item
            </h2>
            <input
              type="text"
              value={editItem.name}
              onChange={(e) =>
                setEditItem({ ...editItem, name: e.target.value })
              }
              className="w-full border px-3 py-2 rounded-lg mb-3"
              placeholder="Product name"
            />
            <input
              type="number"
              value={editItem.quantity}
              onChange={(e) =>
                setEditItem({ ...editItem, quantity: e.target.value })
              }
              className="w-full border px-3 py-2 rounded-lg mb-3"
              placeholder="Quantity"
            />
            <input
              type="number"
              value={editItem.bv}
              onChange={(e) => setEditItem({ ...editItem, bv: e.target.value })}
              className="w-full border px-3 py-2 rounded-lg mb-3"
              placeholder="BV"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setEditItem(null)}
                className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageStock;
